import "assets/fonts/fonts.css";
import "components/common/Form/form-datepicker.css";
import "normalize.css";
import { setConfiguration } from "react-grid-system";
import { createGlobalStyle } from "./index";
import { ITheme } from "./theme.types";

// Declare breakpoints as a enum so it can be used in media queries
export enum Breakpoints {
  xs = 768,
  sm = 1024,
  md = 1280,
  lg = 1440
}

const theme: ITheme = {
  color: {
    primary: "#0c2b51",
    primaryTransparent: "rgba(17, 38, 68, 0.85)",
    secondary: "#c7dff5",
    tertiary: "#a4cf00",
    secondaryTransparent: "rgba(214, 233, 246 , 0.80)",
    white: "#FAFAFA",
    red: "#FF697C",
    gray: { light: "#eaeae7", medium: "#91928e", dark: "#54667f" }
  },
  fontFamily: {
    black: "Avenir-Heavy, sans-serif",
    bold: "Avenir-Heavy, sans-serif",
    light: "Avenir-Light, sans-serif",
    regular: "Avenir, sans-serif"
  },
  fontSize: {
    l: "1.8rem",
    m: "1.6rem",
    ms: "1.5rem",
    s: "1.4rem",
    body: "1.8rem",
    buttons: "1.6rem",
    headings: {
      hero: "4rem",
      h1: "3.75rem",
      h2: "3.4rem",
      h3: "3rem",
      h4: "2.2rem",
      h5: "1.6rem",
      small: "1.2rem"
    }
  },
  global: {
    headerHeight: 85,
    radius: "0.3rem",
    shadow: "0 1px 7px 0 rgba(0, 0, 0, 0.11)",
    transition: "all 200ms ease-in-out",
    nudge: 2,
    gutter: 1.2,
    containerWidths: [960, 960, 1140, 1280],
    keyboard: {
      keyHeight: "4.5vh"
    }
  }
};

export default theme;

// TODO: Solve import module issue and extract to a separate file
const keyboardCss = `

.simple-keyboard {
  font-family: ${theme.fontFamily.regular};
  width: 100%;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  box-sizing: border-box;
  overflow: hidden;
  touch-action: manipulation;
}

.simple-keyboard .hg-row {
  display: flex;
}

.simple-keyboard .hg-row:not(:last-child) {
  margin-bottom: 5px;
}

.simple-keyboard .hg-row .hg-button-container,
.simple-keyboard .hg-row .hg-button:not(:last-child) {
  margin-right: 5px;
}

.simple-keyboard .hg-row > div:last-child {
  margin-right: 0;
}

.simple-keyboard .hg-row .hg-button-container {
  display: flex;
}

.simple-keyboard .hg-button {
  display: inline-block;
  flex-grow: 1;
  cursor: pointer;
}

.simple-keyboard .hg-button span {
  pointer-events: none;
}

.simple-keyboard.hg-theme-default {
  background-color: rgba(0, 0, 0, 0.1);
  padding: 5px;
  border-radius: 5px;
}

.simple-keyboard.hg-theme-default .hg-button {
  box-shadow: 0 0 3px -1px rgba(0, 0, 0, 0.3);
  height: ${theme.global.keyboard.keyHeight};
  min-height: 32px;
  border-radius: 5px;
  box-sizing: border-box;
  padding: 5px;
  background: ${theme.color.white};
  color: ${theme.color.gray.dark};
  border-bottom: 1px solid #b5b5b5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;s
}

.simple-keyboard button.hg-button {
  border-width: 0;
  outline: 0;
  font-size: inherit;
}

.simple-keyboard.hg-theme-default.hg-layout-numeric .hg-button {
  width: 33.3%;
  height: 60px;
  align-items: center;
  display: flex;
  justify-content: center;
}

.simple-keyboard.hg-theme-default .hg-button.hg-button-numpadadd,
.simple-keyboard.hg-theme-default .hg-button.hg-button-numpadenter {
  height: 85px;
}

.simple-keyboard.hg-theme-default .hg-button.hg-button-numpad0 {
  width: 105px;
}

.simple-keyboard.hg-theme-default .hg-button.hg-button-com {
  max-width: 85px;
}

.simple-keyboard.hg-theme-default .hg-button.hg-standardBtn.hg-button-at {
  max-width: 45px;
}

.simple-keyboard.hg-theme-default .hg-button.hg-selectedButton {
  background: rgba(5, 25, 70, 0.53);
  color: ${theme.color.white};
}

.simple-keyboard.hg-theme-default .hg-button.hg-standardBtn[data-skbtn=".com"] {
  max-width: 82px;
}

.simple-keyboard.hg-theme-default .hg-button.hg-standardBtn[data-skbtn="@"] {
  max-width: 60px;
}
`;

export const GlobalStyle = createGlobalStyle`
  html {
    font-size: 62.5%;
    overflow: hidden;
    overflow-scrolling: touch;
    -webkit-overflow-scrolling: touch;
  }
  #root {
    height: 100%;
  }

  .ReactModal__Body--open {
    overflow: hidden;
    overflow-scrolling: touch;
    -webkit-overflow-scrolling: touch;
  }

  body {
    font-family: ${theme.fontFamily.regular}, 'AlternateGotNo3DRegular', sans-serif;
    font-size: ${theme.fontSize.body};
    color: ${theme.color.primary};
    background-color: ${theme.color.white};
    background-size: cover;
    /* Disables pull-to-refresh but allows overscroll glow effects. */
    overscroll-behavior-y: none;

  }
  html, body{
    overflow: hidden;
    position: relative;
    height: 100%;
  }
  a {
    text-decoration: none;
  }
  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.fontFamily.bold};
    color: ${theme.color.primary};
  }
  p {
    margin: ${theme.global.gutter}rem 0;
  }
    ${keyboardCss}
  `;

// Set grid configuration
// See: https://github.com/JSxMachina/react-grid-system#configuration
setConfiguration({
  breakpoints: [Breakpoints.xs, Breakpoints.sm, Breakpoints.md, Breakpoints.lg],
  containerWidths: theme.global.containerWidths,
  gutterWidth: theme.global.gutter * 10
});



// WEBPACK FOOTER //
// ./src/themes/simple-theme.ts